import { CommonConstants } from "../constants/common-constants";
import { HTTPService } from "./httpService";

const api = new HTTPService();
const base = CommonConstants.BaseAddress;

export interface HelpFaq {
  faqId: number;
  question: string;
  answer: string;
  keywords: string;
  displayOrder: number;
}

export interface HelpMessage {
  messageId: number;
  conversationId: number;
  senderType: number; // 0=User 1=Bot 2=Admin
  senderName: string;
  message: string;
  sentAt: string;
}

export interface HelpConversation {
  conversationId: number;
  schoolUserId: number;
  schoolName: string;
  phone?: string;
  callbackQuery?: string;
  status: number; // 0=BotHandled 1=Waiting 2=Active 3=CallbackRequested 4=Resolved
  statusLabel: string;
  createdAt: string;
  acknowledgedAt?: string;
  acknowledgedByName?: string;
  resolvedAt?: string;
  resolvedByName?: string;
  resolutionNote?: string;
  messages: HelpMessage[];
  minutesToAcknowledge?: number;
  minutesToResolve?: number;
}

export const helpService = {
  getFaqs: async (): Promise<HelpFaq[]> => {
    const res = await api.get(`${base}${CommonConstants.ApiConstants.HelpFaqs}`);
    return res.data?.data ?? [];
  },

  createFaq: async (data: Omit<HelpFaq, 'faqId'>): Promise<HelpFaq> => {
    const res = await api.post(`${base}${CommonConstants.ApiConstants.HelpFaqs}`, data);
    return res.data?.data;
  },

  updateFaq: async (faqId: number, data: Omit<HelpFaq, 'faqId'>): Promise<HelpFaq> => {
    const res = await api.put(`${base}${CommonConstants.ApiConstants.HelpFaq(faqId)}`, data);
    return res.data?.data;
  },

  deleteFaq: async (faqId: number): Promise<void> => {
    await api.delete(`${base}${CommonConstants.ApiConstants.HelpFaq(faqId)}`);
  },

  startConversation: async (initialMessage: string): Promise<HelpConversation> => {
    const res = await api.post(`${base}${CommonConstants.ApiConstants.HelpConversations}`, { initialMessage });
    return res.data?.data;
  },

  sendMessage: async (conversationId: number, message: string): Promise<HelpMessage | null> => {
    const res = await api.post(`${base}${CommonConstants.ApiConstants.HelpMessages(conversationId)}`, { message });
    return res.data?.data ?? null;
  },

  getMessages: async (conversationId: number, since?: string): Promise<HelpMessage[]> => {
    const url = since
      ? `${base}${CommonConstants.ApiConstants.HelpMessages(conversationId)}?since=${since}`
      : `${base}${CommonConstants.ApiConstants.HelpMessages(conversationId)}`;
    const res = await api.get(url);
    return res.data?.data ?? [];
  },

  requestCallback: async (conversationId: number, phone: string, query?: string): Promise<void> => {
    await api.post(`${base}${CommonConstants.ApiConstants.HelpCallback(conversationId)}`, { phone, query });
  },

  getSchoolPhones: async (schoolId: string): Promise<string[]> => {
    try {
      const res = await api.get(`${base}/School/Get/${schoolId}`);
      const d = res.data?.data;
      if (!d) return [];
      return [
        { label: `School Contact – ${d.mobileNumber}`, value: d.mobileNumber },
        { label: `Chairman – ${d.chairmanWhatsAppContact}`, value: d.chairmanWhatsAppContact },
        { label: `Principal – ${d.principalWhatsAppContact}`, value: d.principalWhatsAppContact },
        { label: `Programme In-charge – ${d.programInchargeWhatsAppContact}`, value: d.programInchargeWhatsAppContact },
      ]
        .filter(e => e.value?.trim())
        .map(e => e.label);
    } catch {
      return [];
    }
  },

  getConversations: async (status?: number): Promise<HelpConversation[]> => {
    const url = status !== undefined
      ? `${base}${CommonConstants.ApiConstants.HelpConversations}?status=${status}`
      : `${base}${CommonConstants.ApiConstants.HelpConversations}`;
    const res = await api.get(url);
    return res.data?.data ?? [];
  },

  getConversation: async (conversationId: number): Promise<HelpConversation | null> => {
    const res = await api.get(`${base}${CommonConstants.ApiConstants.HelpConversation(conversationId)}`);
    return res.data?.data ?? null;
  },

  resolveConversation: async (conversationId: number, resolutionNote?: string): Promise<void> => {
    await api.patch(`${base}${CommonConstants.ApiConstants.HelpResolve(conversationId)}`, { resolutionNote });
  },
};
